import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { DateTime } from 'luxon';
import { apiGateway } from '../../../../config';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../../../../redux/store';
import type { ErrorResponse } from '../../../../@types/common';
import type { AxiosResponse } from 'axios';
import type { Country, Factory, Item, MailTarget, Port, ShippingSchedule, ComplementedShippingSchedule, NullShippingSchedule } from '../../../../@types/common';
import type { ModalMode, EditData, Delivery, DeliverySet, DeliveryDetail, DeliveryIndex, DeliveryIndexGridData, DeliveryState, DeliveryGridData, DeliveryStatus } from '../../../../@types/delivery';

// ----------------------------------------------------------------------

const initialState: DeliveryState = {
  isLoading: false,
  hasError: false,
  hasWarning: false,
  hasSearched: false,
  message: undefined,
  isModalOpen: false,
  modalMode: undefined,
  options: {
    shippingSchedule: undefined,
    country: undefined,
    factory: undefined,
    item: undefined,
    mailTarget: undefined,
    sourcePort: undefined,
    destinationPort: undefined,
  },
  deliverys: undefined,
  delivery: undefined,
  deliveryPrev: undefined,
  deliveryGridData: undefined,
  deliveryGridDataPrev: undefined,
  editData: {},
  selectedDeliveryDetail: undefined,
  dateFrom: DateTime.now().startOf('month').minus({ months: 2 }).toISO(),
  dateTo: DateTime.now().endOf('month').plus({ months: 12 }).toISO(),
  limit: '1000',
};

const slice = createSlice({
  name: 'delivery',
  initialState,
  reducers: {
    startLoading(state: DeliveryState) {
      state.isLoading = true;
    },

    hasError(state: DeliveryState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasWarning = false;
      state.hasError = true;
      state.message = action.payload;
    },

    hasWarning(state: DeliveryState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasError = false;
      state.hasWarning = true;
      state.message = action.payload;
    },

    resetMessage(state: DeliveryState) {
      state.isLoading = false;
      state.hasError = false;
      state.hasWarning = false;
      state.message = '';
    },

    setModalMode(state: DeliveryState, action: PayloadAction<ModalMode | undefined>) {
      state.modalMode = action.payload;
    },

    openModal(state: DeliveryState, action: PayloadAction<boolean>) {
      state.isModalOpen = action.payload;
    },

// ----------------------------------------------------------------------

    hasSearched(state: DeliveryState, action: PayloadAction<boolean>) {
      state.hasSearched = action.payload;
    },

    changeStartDate(state: DeliveryState, action: PayloadAction<string | undefined>) {
      state.dateFrom = action.payload;
    },

    changeEndDate(state: DeliveryState, action: PayloadAction<string | undefined>) {
      state.dateTo = action.payload;
    },

    changeDeliveryDisplayLimit(state: DeliveryState, action: PayloadAction<string>) {
      state.limit = action.payload;
    },

// ----------------------------------------------------------------------

    getOptionsCountrySuccess(state: DeliveryState, action: PayloadAction<Country[]>) {
      state.isLoading = false;
      state.options.country = action.payload;
    },

    getOptionsFactorySuccess(state: DeliveryState, action: PayloadAction<Factory[]>) {
      state.isLoading = false;
      state.options.factory = action.payload;
    },

    getOptionsItemSuccess(state: DeliveryState, action: PayloadAction<Item[]>) {
      state.isLoading = false;
      state.options.item = action.payload;
    },

    getOptionsMailTargetSuccess(state: DeliveryState, action: PayloadAction<MailTarget[]>) {
      state.isLoading = false;
      state.options.mailTarget = action.payload;
    },

    getOptionsPortSuccess(state: DeliveryState, action: PayloadAction<Port[]>) {
      state.isLoading = false;
      const allPorts: Port[] = action.payload;
      state.options.sourcePort = allPorts.filter(port => port.isSourcePort === true).filter(port => port.code !== 'KZU');
      state.options.destinationPort = allPorts.filter(port => port.isDestinationPort === true);
    },

    getOptionsShippingScheduleSuccess(state: DeliveryState, action: PayloadAction<ShippingSchedule[]>) {
      state.isLoading = false;
      const complementedShippingSchedule: ComplementedShippingSchedule[] = action.payload.map(schedule => {
        const id: string = String(schedule.id);
        const bookingNumber: string = schedule.bookingNumber || 'TBA';
        const shippingName: string = schedule.shippingName || '';
        const cyOpenDate: string = schedule.cyOpenDate?.split('T00:00:00.000Z').join('').split('-').join('/') || '';
        const cutDate: string = schedule.cutDate?.split('T00:00:00.000Z').join('').split('-').join('/') || '';
        const etdDate: string = schedule.etdDate?.split('T00:00:00.000Z').join('').split('-').join('/') || '';
        const etaDate: string = schedule.etaDate?.split('T00:00:00.000Z').join('').split('-').join('/') || '';
        const containerNumber: number = schedule.containerNumber || 0;
        const temporaryDisplayName: string = `${bookingNumber} ・ ${shippingName} ・ ${cyOpenDate} ・ ${cutDate} ・ ${etdDate}・ ${etaDate}  ・ ${containerNumber}`;
        return {
          ...schedule,
          temporaryDisplayName,
        };
      });
      const sortedShippingSchedule: ComplementedShippingSchedule[] = complementedShippingSchedule.sort((a: ComplementedShippingSchedule, b: ComplementedShippingSchedule) => {
        if(a.etdDate && b.etdDate && (a.etdDate < b.etdDate)) {
          return -1;
        } else if(a.etdDate && b.etdDate && (a.etdDate > b.etdDate)) {
          return 1;
        } else {
          return 0;
        }
      });
      // null用のShippingScheduleを削除
      const exceptNullShippingSchedule: ComplementedShippingSchedule[] | undefined = state.options.shippingSchedule?.filter((schedule): schedule is ComplementedShippingSchedule => {
        return (schedule as ComplementedShippingSchedule).id !== null;
      });
      const concatedShippingSchedule: ComplementedShippingSchedule[] = exceptNullShippingSchedule
        ? [...exceptNullShippingSchedule, ...sortedShippingSchedule]
        : sortedShippingSchedule;
      // 重複を取り除く
      const shippingScheduleObject = concatedShippingSchedule.reduce((acc: { [key: number]: ComplementedShippingSchedule }, cur: ComplementedShippingSchedule) => {
        acc[cur.id] = cur;
        return acc;
      }, {});
      const shippingSchedule: ComplementedShippingSchedule[] = Object.values(shippingScheduleObject);
      const nullShippingSchedule: NullShippingSchedule = {
        id: null,
        temporaryDisplayName: '(blank)',
        isShipped: false,
      }
      state.options.shippingSchedule = [...shippingSchedule, nullShippingSchedule];
    },

// ----------------------------------------------------------------------

    getDeliveriesSuccess(state: DeliveryState, action: PayloadAction<DeliveryIndex[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      const deliverys: DeliveryIndexGridData[] = action.payload.map(delivery => {
        const { id, deliveryNumber, deliveryFactoryCode, shippingPortCode, destinationPortCode, status, updatedAt } = delivery;
        return {
          id,
          deliveryNumber,
          deliveryFactoryCode,
          shippingPortCode,
          destinationPortCode,
          status,
          updatedAt,
          orderYearMonth: delivery.orderHeader?.orderYearMonth || null,
          orderNumber: delivery.orderHeader?.orderNumber || null,
          orderType: delivery.orderHeader?.orderType || null,
        };
      });
      state.deliverys = deliverys;
    },

    getDeliverySuccess(state: DeliveryState, action: PayloadAction<DeliverySet>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      state.delivery = action.payload.delivery;
      // deliveryからdeliveryGridDataを作成してstateにセット
      const delivery: Delivery = action.payload.delivery;
      const deliveryGridData: DeliveryGridData[]  = delivery.deliveryDetail.map(deliveryDetail => {
        const gridData: DeliveryGridData = getGridData(deliveryDetail);
        return gridData;
      });
      // console.log(deliveryGridData);
      state.deliveryGridData = deliveryGridData;
      state.editData = Object.assign(state.editData, {id: action.payload.delivery.id});
      // deliveryPrevからdeliveryGridDataPrevを作成してstateにセット
      const deliveryPrev: Delivery = action.payload.deliveryPrev;
      const deliveryGridDataPrev: DeliveryGridData[]  = deliveryPrev.deliveryDetail.map(deliveryDetail => {
        const gridData: DeliveryGridData = getGridData(deliveryDetail);
        return gridData;
      });
      // console.log(deliveryGridDataPrev);
      state.deliveryGridDataPrev = deliveryGridDataPrev;
    },

    updateDeliverySuccess(state: DeliveryState, action: PayloadAction<DeliverySet>) {
      // state.message以外はgetDeliverySuccessと同じ
      state.isLoading = false;
      state.hasError = false;
      state.message = 'successfully updated';
      state.delivery = action.payload.delivery;
      // deliveryからdeliveryGridDataを作成してstateにセット
      const delivery: Delivery = action.payload.delivery;
      const deliveryGridData: DeliveryGridData[]  = delivery.deliveryDetail.map(deliveryDetail => {
        const gridData: DeliveryGridData = getGridData(deliveryDetail);
        return gridData;
      });
      // console.log(deliveryGridData);
      state.deliveryGridData = deliveryGridData;
      state.editData = Object.assign(state.editData, {id: action.payload.delivery.id});
      // deliveryPrevからdeliveryGridDataPrevを作成してstateにセット
      const deliveryPrev: Delivery = action.payload.deliveryPrev;
      const deliveryGridDataPrev: DeliveryGridData[]  = deliveryPrev.deliveryDetail.map(deliveryDetail => {
        const gridData: DeliveryGridData = getGridData(deliveryDetail);
        return gridData;
      });
      // console.log(deliveryGridDataPrev);
      state.deliveryGridDataPrev = deliveryGridDataPrev;
    },

    confirmDeliverySuccess(state: DeliveryState, action: PayloadAction<Delivery>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = 'successfully requested';
      // editDataをリセットする
      state.editData = Object.assign({}, {id: action.payload.id});
      // ※出荷一覧は単票情報ではないので更新しない　→　再検索で対応
    },

    rejectDeliverySuccess(state: DeliveryState, action: PayloadAction<Delivery>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = 'successfully rejected';
      // editDataをリセットする
      state.editData = Object.assign({}, {id: action.payload.id});
      // ※出荷一覧は単票情報ではないので更新しない　→　再検索で対応
    },

    approveDeliverySuccess(state: DeliveryState, action: PayloadAction<Delivery>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = 'successfully approved';
      // editDataをリセットする
      state.editData = Object.assign({}, {id: action.payload.id});
      // ※出荷一覧は単票情報ではないので更新しない　→　再検索で対応
    },
  }
});

export default slice.reducer;

function getGridData(deliveryDetail: DeliveryDetail): DeliveryGridData {
  const { id } = deliveryDetail;
  return {
    id,
    destinationPortCode: deliveryDetail.orderDetail ? deliveryDetail.orderDetail.destinationPortCode : null,
    cutDateTokyo: deliveryDetail.orderDetail ? deliveryDetail.orderDetail.cutDateTokyo : null,
    cutDateKobe: deliveryDetail.orderDetail ? deliveryDetail.orderDetail.cutDateKobe : null,
    itemCode: deliveryDetail.orderDetail ? deliveryDetail.orderDetail.itemCode : null,
    amount: deliveryDetail.amount,
    orderUnit: deliveryDetail.orderUnit,
    showaItemCode: deliveryDetail.item ? deliveryDetail.item.showaItemCode : null,
    specialCustomer: deliveryDetail.orderDetail ? deliveryDetail.orderDetail.specialCustomer : null,
    assortmentCode: deliveryDetail.orderDetail ? deliveryDetail.orderDetail.assortmentCode : null,
    assortmentAmount: deliveryDetail.assortmentAmount,
    assortmentWeight: deliveryDetail.assortmentWeight,
    makerDeliveryDate: deliveryDetail.makerDeliveryDate || null,
    deliveryFactoryCode: deliveryDetail.deliveryFactoryCode,
    isRokko: deliveryDetail.isRokko,
    shippingPortCode: deliveryDetail.shippingPortCode,
    shippingScheduleId: deliveryDetail.shippingScheduleId,
    containerNumber: deliveryDetail.containerNumber,
    comment: deliveryDetail.comment,
    orderDetailId: deliveryDetail.orderDetailId,
    orderDetailAmount: deliveryDetail.orderDetail ? deliveryDetail.orderDetail.amount : null,
    orderDetailUnit: deliveryDetail.orderDetail ? deliveryDetail.orderDetail.unit : null,
    bagsPerFcl: deliveryDetail.item ? deliveryDetail.item.bagsPerFcl : null,
    weightPerBag: deliveryDetail.item ? deliveryDetail.item.weightPerBag : null,
    createdBy: deliveryDetail.createdBy,
    createdAt: deliveryDetail.createdAt,
    updatedBy: deliveryDetail.updatedBy ? deliveryDetail.updatedBy : null,
    updatedAt: deliveryDetail.updatedAt ? deliveryDetail.updatedAt : null,
    isDeleted: deliveryDetail.isDeleted,
  }
}


// TODO: https://stackoverflow.com/questions/63439021/handling-errors-with-redux-toolkit

// ----------------------------------------------------------------------

export function setModalMode(mode?: ModalMode): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.setModalMode(mode));
  }
};

export function openModal(open: boolean): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.openModal(open));
  }
};

export function hasError(message: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.hasError(message));
  }
};

export function hasWarning(message: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.hasWarning(message));
  }
};

export function resetMessage(): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  }
};

export function changeStartDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeStartDate(date));
  }
};

export function changeEndDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeEndDate(date));
  }
};

export function changeDeliveryDisplayLimit(limit: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeDeliveryDisplayLimit(limit));
  }
};

// ----------------------------------------------------------------------

export function getOptionsCountry(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Country[]>(
        apiGateway + '/options/country',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsCountrySuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsFactory(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Factory[]>(
        apiGateway + '/options/factory',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsFactorySuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsItem(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Item[]>(
        apiGateway + '/options/item',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsItemSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsMailTarget(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<MailTarget[]>(
        apiGateway + '/options/mailTarget',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsMailTargetSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsPort(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Port[]>(
        apiGateway + '/options/port',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsPortSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsShippingSchedule(ids?: number[]): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const params = ids
      ? {
        'where': {
          'id': {
            'in': ids
          }
        },
      }
      : {};
      const response: AxiosResponse = await axios.get<ShippingSchedule[]>(
        apiGateway + '/shipping',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          params: {
            filter: JSON.stringify(params)
          },
          timeout: 60000
        }
      );
      // console.log(response.data);
      dispatch(slice.actions.getOptionsShippingScheduleSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};
  
export function getOptionsUnshippedShippingSchedule(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const params = {
        'where': {
          'isShipped': false
        },
      };
      const response: AxiosResponse = await axios.get<ShippingSchedule[]>(
        apiGateway + '/shipping',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          params: {
            filter: JSON.stringify(params)
          },
          timeout: 60000
        }
      );
      // console.log(response.data);
      dispatch(slice.actions.getOptionsShippingScheduleSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

// ----------------------------------------------------------------------

export function getDeliveries(dateFrom?: string, dateTo?: string, limit?: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<DeliveryIndex[]>(
        apiGateway + '/delivery/search',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          params: {
            dateFrom,
            dateTo,
            limit
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.hasSearched(true));
      dispatch(slice.actions.getDeliveriesSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
    // setTimeout(() => {
    //   dispatch(slice.actions.getDeliveriesSuccess([deliveryData[0]]));
    // } , 2000);
  };
};

export function getDelivery(id: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<DeliverySet>(
        apiGateway + '/delivery/' + id,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getDeliverySuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
    // setTimeout(() => {
    //   dispatch(slice.actions.getDeliverySuccess(deliveryData[0]));
    // } , 2000);
  };
};

export function updateDelivery(requestParams: EditData, nextStatus?: DeliveryStatus): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    let params: EditData = requestParams;
    // 必要に応じてstatusを設定
    if(nextStatus){
      params.status = nextStatus;
    }
    // console.log(params);
    // リクエスト
    const response: AxiosResponse = await axios.put<DeliverySet>(
      apiGateway + '/delivery/withDetail/' + params.id,
      params,
      {
        headers: {
          'Authorization': 'Bearer ' + token,
        },
        timeout: 60000
      }
    );
    dispatch(slice.actions.updateDeliverySuccess(response.data));
  };
};

// ----------------------------------------------------------------------

export function confirmDelivery(
  requestParams: EditData,
  isUrgent: boolean,
  ids: number[],
  message: string,
): AppThunk {
  return async (dispatch, getState): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const id: number | undefined = getState().delivery.delivery?.id;
    const mode: ModalMode | undefined = getState().delivery.modalMode;
    const nextStatus: DeliveryStatus | undefined = mode === 'NOTIFY'
      ? 'DELIVERY_PREPARING'
      : mode === 'CONFIRM'
        ? 'MAKER_TTC_DELIVERY_CONFIRMED'
        : undefined;
    if(id && nextStatus) {
      // 編集パラメータを設定する
      const editParams: EditData = requestParams;
      // 確認パラメータを設定する
      const confirmParams = {
        isUrgent,
        status: nextStatus,
        mailTarget: ids,
        message,
      };
      // リクエストパラメータ　＝　編集パラメータ　＋　確認パラメータ
      const params = Object.assign({}, editParams, confirmParams);
      // console.log('dispatch confirm delivery: ', params);
      // リクエスト
      const response: AxiosResponse = await axios.put<Delivery>(
        apiGateway + '/delivery/confirmDeliveryWithDetail/' + id,
        params,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.confirmDeliverySuccess(response.data));
    } else {
      dispatch(slice.actions.hasError('canT find delivery or modal mode'));
    }
  };
};

export function rejectDelivery(requestParams: EditData, ids: number[]): AppThunk {
  return async (dispatch, getState): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const id: number | undefined = getState().delivery.delivery?.id;
    if(id) {
      // 編集パラメータを設定する
      const editParams: EditData = requestParams;
      // 否認パラメータを設定する
      const rejectParams = {
        status: 'DELIVERY_PREPARING',
        mailTarget: ids,
      };
      // リクエストパラメータ　＝　編集パラメータ　＋　否認パラメータ
      const params = Object.assign({}, editParams, rejectParams);
      // console.log('dispatch reject delivery: ', editParams, rejectParams, params);
      // リクエスト
      const response: AxiosResponse = await axios.put<Delivery>(
        apiGateway + '/delivery/rejectWithDetail/' + id,
        params,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.rejectDeliverySuccess(response.data));
    }
  };
};

export function approveDelivery(requestParams: EditData, ids: number[]): AppThunk {
  return async (dispatch, getState): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const id: number | undefined = getState().delivery.delivery?.id;
    if(id) {
      // 編集パラメータを設定する
      const editParams: EditData = requestParams;
      // 承認パラメータを設定する
      const approveParams = {
        status: 'TTC_SHIWO_DELIVERY_CONFIRMED',
        mailTarget: ids,
      };
      // リクエストパラメータ　＝　編集パラメータ　＋　承認パラメータ
      const params = Object.assign({}, editParams, approveParams);
      // console.log('dispatch approve delivery: ', params);
      // リクエスト
      const response: AxiosResponse = await axios.put<Delivery>(
        apiGateway + '/delivery/approveDeliveryWithDetail/' + id,
        params,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.approveDeliverySuccess(response.data));
    }
  };
};

// ----------------------------------------------------------------------
